import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import { columnTree, showValue, type ColumnInfo, type ColumnNode } from "@/lib/integrations/settings";

import { SearchField } from "./controls";
import styles from "./integrations.module.css";

function percent(filled: number, total: number): string {
  if (total <= 0) return "—";
  return `${Math.round((filled / total) * 100)}%`;
}

function Node({
  node,
  total,
  selected,
  onPick,
  open,
  toggle,
}: {
  node: ColumnNode;
  total: number;
  selected: string | null | undefined;
  onPick: (path: string) => void;
  open: Set<string>;
  toggle: (path: string) => void;
}) {
  const hasChildren = node.children.length > 0;
  const isOpen = open.has(node.path);
  const column = node.column;
  return (
    <li>
      <button
        type="button"
        className={selected === node.path ? `${styles.treeRow} ${styles.treeRowOn}` : styles.treeRow}
        aria-expanded={hasChildren ? isOpen : undefined}
        onClick={() => (column && !hasChildren ? onPick(node.path) : toggle(node.path))}
        title={column ? `${node.path} — filled in ${percent(column.filled, total)} of rows` : node.path}
      >
        <span className={styles.icon} aria-hidden>
          {hasChildren ? isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} /> : <span style={{ width: 14 }} />}
        </span>
        <span style={{ minWidth: 0 }}>
          <span className={styles.treeName}>{node.name}</span>
          {column && column.samples.length > 0 && (
            <span className={styles.treeSamples}>{column.samples.map((sample) => showValue(sample, 30)).join(" · ")}</span>
          )}
        </span>
        <span className={styles.treeFilled}>{column ? percent(column.filled, total) : `${node.children.length} inside`}</span>
      </button>
      {hasChildren && column && isOpen && (
        <button type="button" className={styles.linkish} style={{ marginLeft: "var(--s5)" }} onClick={() => onPick(node.path)}>
          Use {node.path} itself
        </button>
      )}
      {hasChildren && isOpen && (
        <ul className={styles.tree}>
          {node.children.map((child) => (
            <Node key={child.path} node={child} total={total} selected={selected} onPick={onPick} open={open} toggle={toggle} />
          ))}
        </ul>
      )}
    </li>
  );
}

/** What Scout sent, as a tree: each column, a few of its values, and how often it is filled. */
export function ColumnTree({
  columns,
  total,
  selected,
  onPick,
}: {
  columns: ColumnInfo[];
  /** Rows the fill share is measured over. */
  total: number;
  selected?: string | null;
  onPick: (path: string) => void;
}) {
  const [search, setSearch] = useState("");
  const needle = search.trim().toLowerCase();
  const tree = useMemo(
    () => columnTree(needle ? columns.filter((column) => column.path.toLowerCase().includes(needle)) : columns),
    [columns, needle],
  );
  const [open, setOpen] = useState<Set<string>>(() => {
    const start = new Set<string>();
    if (selected) {
      const parts = selected.split(".");
      for (let i = 1; i < parts.length; i += 1) start.add(parts.slice(0, i).join("."));
    }
    return start;
  });
  const toggle = (path: string) =>
    setOpen((was) => {
      const next = new Set(was);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  // While searching every branch is open, so a hit deep inside is visible.
  const shown = needle ? new Set(columns.flatMap((column) => prefixes(column.path))) : open;
  return (
    <div style={{ display: "grid", gap: "var(--s2)" }}>
      <SearchField
        placeholder="Find a column"
        value={search}
        aria-label="Find a column"
        onChange={(event) => setSearch(event.target.value)}
      />
      <div className={styles.scrollBox} style={{ padding: "var(--s1)" }}>
        {tree.length === 0 ? (
          <p className={styles.faint} style={{ padding: "var(--s2)" }}>
            {columns.length === 0 ? "Scout sent no columns in this run." : "No column has that name."}
          </p>
        ) : (
          <ul className={styles.tree}>
            {tree.map((node) => (
              <Node key={node.path} node={node} total={total} selected={selected} onPick={onPick} open={shown} toggle={toggle} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function prefixes(path: string): string[] {
  const parts = path.split(".");
  return parts.map((_, i) => parts.slice(0, i + 1).join("."));
}
